import { Injectable } from '@angular/core';

import { BettingOfferApi } from '@frontend/sports/betting-offer/feature/offer-service';
import { Nullable } from '@frontend/sports/common/core/utils/extended-types';
import { IPayloadAction } from '@frontend/sports/common/core/utils/redux';
import { TimerService } from '@frontend/sports/common/core/utils/timer';
import { Store } from '@ngrx/store';
import { firstValueFrom } from 'rxjs';

import { IBetslipState } from '../../../base/store/state';
import { BetslipV1Pick } from '../../../core/picks/betslip-v1-pick';
import { V1PickId } from '../../../core/picks/pick-id';
import { ResultStatus } from '../../../core/picks/pick-models';
import BetslipPickFactory from '../../picks/factory/betslip-pick.factory';
import { BetslipPicksRequestBuilderService } from '../../picks/services/betslip-pick-request-builder.service';
import { EditBetActions, ISwapPickPayload } from '../actions';

@Injectable()
export class EditBetPickRefreshService {
    private pickSchedules: BetslipV1Pick[] = [];
    private scheduleClean: Nullable<() => void> = null;

    constructor(
        private store: Store,
        private bettingOffer: BettingOfferApi,
        private betslipPicksRequestBuilder: BetslipPicksRequestBuilderService,
        private pickFactory: BetslipPickFactory,
        private timerService: TimerService,
    ) {}

    /**
     * Check picks.
     *
     * @param picks picks for check
     * @return new picks
     */
    private async checkPicks(picks: BetslipV1Pick[]): Promise<ISwapPickPayload[]> {
        const pickIds = picks.map((p) => p.id);
        if (pickIds.length === 0) {
            return [];
        }
        const request = this.betslipPicksRequestBuilder.buildRequestFromPickId(pickIds, true);
        const response = await firstValueFrom(this.bettingOffer.getPicksWithFallback(request));
        if (!response) {
            return [];
        }

        // We are going to update all pre-match pics on cut off date, when we can mark the pick as closed.
        // Or if the cut off date is moved to the future it will be updated.
        const actions: IPayloadAction<ISwapPickPayload>[] = [];
        for (const oldPick of picks) {
            const liveFallback = response.liveFallbacks[oldPick.id.optionId];
            const fixture = liveFallback
                ? response.fixturePage.fixtures.find(
                      (f) =>
                          f.id === liveFallback.fixtureId &&
                          (f.hybridFixtureData != null
                              ? f.optionMarkets.some((g) => g.id === liveFallback.gameId && g.options.some((r) => r.id === liveFallback.resultId))
                              : f.games.some((g) => g.id === liveFallback.gameId && g.results.some((r) => r.id === liveFallback.resultId))),
                  )
                : response.fixturePage.fixtures.find(
                      (f) =>
                          f.id === oldPick.event.id &&
                          (f.hybridFixtureData != null
                              ? f.optionMarkets.some((g) => g.id === oldPick.market.id && g.options.some((r) => r.id === oldPick.option.id))
                              : f.games.some((g) => g.id === oldPick.market.id && g.results.some((r) => r.id === oldPick.option.id))),
                  );
            if (fixture) {
                const newPickId = liveFallback ? new V1PickId(liveFallback.fixtureId, liveFallback.gameId, liveFallback.resultId) : oldPick.id.copy();
                const newPick = this.pickFactory.createNewPickFromOldAndBcdPick(oldPick, newPickId, fixture);
                if (newPick) {
                    actions.push(EditBetActions.swapPick({ payload: { oldPick, newPick, highlightNewPick: false } }));
                }
            } else {
                // no fixture pre match is closed.
                const newPick = oldPick.copy();
                newPick.market.isVisible = false;
                actions.push(EditBetActions.swapPick({ payload: { oldPick, newPick, highlightNewPick: false } }));
            }
        }

        for (const action of actions) {
            this.store.dispatch(action);
        }

        return actions.map((a) => a.payload);
    }

    private splitPicks(date: Date): { oldPicks: BetslipV1Pick[]; futurePicks: BetslipV1Pick[] } {
        const oldPicks: BetslipV1Pick[] = [];
        const futurePicks: BetslipV1Pick[] = [];
        for (const pick of this.pickSchedules) {
            if (pick.event.cutOffDate <= date) {
                oldPicks.push(pick);
            } else {
                futurePicks.push(pick);
            }
        }

        return { oldPicks, futurePicks };
    }

    private createSchedule(): void {
        this.scheduleClean && this.scheduleClean();
        if (this.pickSchedules.length === 0) {
            return;
        }
        const now = new Date();
        const minCutOffDate = this.pickSchedules.reduce((date, p) => {
            return p.event.cutOffDate < date ? p.event.cutOffDate : date;
        }, this.pickSchedules[0].event.cutOffDate);
        const dueTime = minCutOffDate.valueOf() - now.valueOf() + 5000; // Add 5 sec if the local time has small difference with server time.

        this.scheduleClean = this.timerService.setTimeout(
            async () => {
                const now1 = new Date();
                const { oldPicks, futurePicks } = this.splitPicks(now1);
                const payloads = await this.checkPicks(oldPicks);
                this.pickSchedules = [
                    ...futurePicks,
                    ...payloads
                        .map((p) => p.newPick as BetslipV1Pick)
                        .filter((p) => !p.isLive && p.resultStatus === ResultStatus.Open && p.event.cutOffDate > now1),
                ];
                this.scheduleClean = null;
                this.createSchedule();
            },
            Math.max(0, dueTime),
        );
    }

    async updatePicks(betslipState: IBetslipState): Promise<ISwapPickPayload[]> {
        // Check only V1, not resulted, pre-match picks.
        const picks = betslipState.types.editBet.picks.pickList.filter(
            (p) => BetslipV1Pick.isPick(p) && !p.isLive && p.resultStatus === ResultStatus.Open,
        ) as BetslipV1Pick[];
        // Check removed, so if odds of live version are good the user may decide to add them again.

        this.cleanSchedules(); // Clean all timers

        const picksForCheck = picks.filter((p) => {
            const pickId = p.id.toString();
            const editBetPick = betslipState.types.editBet.picks.pickState[pickId];

            return editBetPick.resultedState === ResultStatus.Open;
        });

        const payloads = await this.checkPicks(picksForCheck);

        const notSwappedButUpdatedPicks = picksForCheck
            .filter((p) => payloads.some((swap) => swap.oldPick.id.isEqual(p.id) && swap.newPick.id.isEqual(p.id)))
            .map((p) => {
                // Get updated version.
                const newPick = payloads.find((swap) => swap.newPick.id.isEqual(p.id))!.newPick as BetslipV1Pick;

                return newPick;
            });

        // Continue checking picks which are just updated.
        this.scheduleForCheck(notSwappedButUpdatedPicks);

        return payloads;
    }

    scheduleForCheck(picks: BetslipV1Pick[]): void {
        this.pickSchedules = picks;
        this.createSchedule();
    }

    cleanSchedules(): void {
        this.pickSchedules = [];
        this.scheduleClean && this.scheduleClean();
        this.scheduleClean = null;
    }
}
